import { useEffect, useState } from "react";
import { isUkCountry } from "../isUkCountry";

// Free, keyless, CORS-enabled IP geolocation endpoint.
// Returns e.g. { "country": "GB", "ip": "...", "name": "United Kingdom" }
const IP_COUNTRY_LOOKUP_URL = "https://get.geojs.io/v1/ip/country.json";

// Cached at module level so the lookup runs at most once per page load,
// no matter how many components use the hook. `undefined` = not fetched yet,
// `null` = fetched but unresolved (network error / blocked).
let cachedCountry: string | null | undefined;
let pendingLookup: Promise<string | null> | undefined;

function lookupIpCountry(): Promise<string | null> {
  if (cachedCountry !== undefined) return Promise.resolve(cachedCountry);

  if (!pendingLookup) {
    pendingLookup = fetch(IP_COUNTRY_LOOKUP_URL)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        const country: string | null = typeof data?.country === "string" ? data.country : null;
        cachedCountry = country;
        return country;
      })
      .catch(() => {
        cachedCountry = null;
        return null;
      });
  }

  return pendingLookup;
}

/**
 * Whether the current visitor is browsing from the UK, based on an IP
 * geolocation lookup. Works pre-login (no account data required). Returns
 * false until the lookup resolves, and stays false if it fails — so
 * non-UK content is always the fallback.
 */
export default function useIsUserFromUk(): boolean {
  const [country, setCountry] = useState<string | null>(cachedCountry ?? null);

  useEffect(() => {
    let active = true;
    lookupIpCountry().then((result) => {
      if (active) setCountry(result);
    });
    return () => {
      active = false;
    };
  }, []);

  return isUkCountry(country);
}
